import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import type { ActivityFormData } from '../ActivityEditorPage.types';
import styles from './TypeSpecificFields.module.scss';

interface TranslationTapFieldsProps {
  sourceText: string;
  words: string[];
  correctAnswers: string[];
  onChange: (updates: Partial<ActivityFormData>) => void;
  errors?: {
    sourceText?: string;
    words?: string;
    correctAnswers?: string;
  };
}

export function TranslationTapFields({
  sourceText,
  words,
  correctAnswers,
  onChange,
  errors,
}: TranslationTapFieldsProps) {
  const handleWordChange = (index: number, value: string) => {
    const newWords = [...words];
    newWords[index] = value;
    onChange({ words: newWords });
  };

  const handleAddWord = () => {
    onChange({ words: [...words, ''] });
  };

  const handleRemoveWord = (index: number) => {
    if (words.length <= 2) return;
    onChange({ words: words.filter((_, i) => i !== index) });
  };

  const handleAnswerChange = (index: number, value: string) => {
    const newAnswers = [...correctAnswers];
    newAnswers[index] = value;
    onChange({ correctAnswers: newAnswers });
  };

  const handleAddAnswer = () => {
    onChange({ correctAnswers: [...correctAnswers, ''] });
  };

  const handleRemoveAnswer = (index: number) => {
    if (correctAnswers.length <= 1) return;
    onChange({ correctAnswers: correctAnswers.filter((_, i) => i !== index) });
  };

  return (
    <div className={styles.typeFields}>
      <h3 className={styles.typeFields__title}>Traduce la frase (palabras con tap)</h3>
      <p className={styles.typeFields__description}>
        El alumno ve la frase en español y construye la traducción al inglés tocando chips de palabras.
        Las palabras del pool incluyen la traducción correcta más distractores.
      </p>

      <div>
        <label htmlFor="sourceTextTT" className={styles.typeFields__title}>
          Frase en español (del script)
        </label>
        <Input
          id="sourceTextTT"
          value={sourceText}
          onChange={(e) => onChange({ sourceText: e.target.value })}
          placeholder="Ej: Ella va al mercado cada domingo."
        />
        {errors?.sourceText && (
          <span className={styles.typeFields__error}>{errors.sourceText}</span>
        )}
      </div>

      <div>
        <label className={styles.typeFields__title}>
          Palabras de la traducción correcta en orden ({correctAnswers.length})
        </label>
        <p className={styles.typeFields__description}>
          Las palabras que forman la traducción correcta, en orden.
        </p>
        <div className={styles.typeFields__list}>
          {correctAnswers.map((answer, index) => (
            <div key={index} className={styles.typeFields__optionItem}>
              <span style={{ minWidth: '24px', textAlign: 'center', color: '#6B7280' }}>
                {index + 1}.
              </span>
              <div className={styles.typeFields__optionInput}>
                <Input
                  value={answer}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                  placeholder={`Palabra ${index + 1} de la traducción`}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                leftIcon={<Trash2 />}
                onClick={() => handleRemoveAnswer(index)}
                disabled={correctAnswers.length <= 1}
                aria-label={`Eliminar palabra correcta ${index + 1}`}
              >
                <span className="sr-only">Eliminar</span>
              </Button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          leftIcon={<Plus />}
          onClick={handleAddAnswer}
          className={styles.typeFields__addButton}
        >
          Añadir palabra correcta
        </Button>
        {errors?.correctAnswers && (
          <span className={styles.typeFields__error}>{errors.correctAnswers}</span>
        )}
      </div>

      <div>
        <label className={styles.typeFields__title}>
          Pool completo de palabras ({words.length}) — correctas + distractores
        </label>
        <p className={styles.typeFields__description}>
          Incluye todas las palabras de la traducción correcta más 2–3 distractores. Se mostrarán barajadas.
        </p>
        <div className={styles.typeFields__list}>
          {words.map((word, index) => (
            <div key={index} className={styles.typeFields__optionItem}>
              <span style={{ minWidth: '24px', textAlign: 'center', color: '#6B7280' }}>
                {index + 1}.
              </span>
              <div className={styles.typeFields__optionInput}>
                <Input
                  value={word}
                  onChange={(e) => handleWordChange(index, e.target.value)}
                  placeholder={`Palabra ${index + 1}`}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                leftIcon={<Trash2 />}
                onClick={() => handleRemoveWord(index)}
                disabled={words.length <= 2}
                aria-label={`Eliminar palabra ${index + 1}`}
              >
                <span className="sr-only">Eliminar</span>
              </Button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          leftIcon={<Plus />}
          onClick={handleAddWord}
          className={styles.typeFields__addButton}
        >
          Añadir palabra
        </Button>
        {errors?.words && <span className={styles.typeFields__error}>{errors.words}</span>}
      </div>
    </div>
  );
}
